import { z } from "zod/v4";

/**
 * Onboarding Questionnaire Configuration
 *
 * - is_mandatory: true = hide skip button, false = show skip button
 * - select_multiple: true = multi-select, false = single-select
 * - auto_advance: false = require Continue for a single-select question
 * - text_input: option-level free-text prompt shown only when selected
 */

export interface T_OnboardingOptionTextInput {
  placeholder?: string;
  optional: boolean;
  max_length?: number;
}

export const ONBOARDING_OPTION_ICON_VALUES = [
  "reddit",
  "twitter_x",
  "facebook",
  "instagram",
  "google",
  "tiktok",
  "youtube",
  "app_store",
  "discord",
  "stocktwits",
  "threads",
  "word_of_mouth",
  "podcast",
  "brokerage",
  "photo",
  "file",
  "manual",
  "other",
] as const;

export type T_OnboardingOptionIcon =
  (typeof ONBOARDING_OPTION_ICON_VALUES)[number];

export interface T_OnboardingOption {
  value: string;
  text: string;
  description?: string;
  icon?: T_OnboardingOptionIcon;
  text_input?: T_OnboardingOptionTextInput;
}

export interface T_OnboardingQuestion {
  key: string;
  question: string;
  description?: string;
  options: T_OnboardingOption[] | string[];
  select_multiple: boolean;
  is_mandatory: boolean;
  auto_advance?: boolean;
}

export interface T_OnboardingQuestionnaire {
  num_questions: number;
  questions: T_OnboardingQuestion[];
}

export const ONBOARDING_INTERESTED_INDUSTRY_VALUES = [
  "artificial_intelligence",
  "semiconductors",
  "software_cloud",
  "healthcare_biotech",
  "clean_energy",
  "financials_fintech",
  "consumer_brands",
  "real_estate",
  "industrials_infrastructure",
  "energy_commodities",
  "defense_aerospace",
  "crypto_digital_assets",
] as const;

export const ONBOARDING_INVESTING_YEAR_VALUES = [
  "LESS_THAN_2",
  "TWO_TO_FIVE",
  "FIVE_TO_TEN",
  "TEN_PLUS",
] as const;

export const ONBOARDING_OTHER_SOURCE_MAX_LENGTH = 120;

export const ONBOARDING_HEARD_ABOUT_SOURCE_VALUES = [
  "REDDIT",
  "TWITTER_X",
  "FACEBOOK",
  "INSTAGRAM",
  "GOOGLE",
  "TIKTOK",
  "YOUTUBE",
  "APP_STORE",
  "DISCORD",
  "STOCKTWITS",
  "THREADS",
  "WORD_OF_MOUTH",
  "PODCAST",
  "OTHER",
] as const;

export const ONBOARDING_AGE_GROUP_VALUES = [
  "under_18",
  "18_24",
  "25_34",
  "35_44",
  "45_54",
  "55_64",
  "65_plus",
] as const;

export type T_OnboardingInterestedIndustry =
  (typeof ONBOARDING_INTERESTED_INDUSTRY_VALUES)[number];

export type T_OnboardingInvestingYears =
  (typeof ONBOARDING_INVESTING_YEAR_VALUES)[number];

export type T_OnboardingHeardAboutSource =
  (typeof ONBOARDING_HEARD_ABOUT_SOURCE_VALUES)[number];

export type T_OnboardingAgeGroup = (typeof ONBOARDING_AGE_GROUP_VALUES)[number];

export type T_OnboardingAgeRange = {
  min_age: number;
  max_age: number;
};

export const ONBOARDING_AGE_GROUP_META: Record<
  T_OnboardingAgeGroup,
  T_OnboardingAgeRange & { text: string }
> = {
  under_18: { text: "Under 18", min_age: 0, max_age: 17 },
  "18_24": { text: "18 - 24", min_age: 18, max_age: 24 },
  "25_34": { text: "25 - 34", min_age: 25, max_age: 34 },
  "35_44": { text: "35 - 44", min_age: 35, max_age: 44 },
  "45_54": { text: "45 - 54", min_age: 45, max_age: 54 },
  "55_64": { text: "55 - 64", min_age: 55, max_age: 64 },
  "65_plus": { text: "65+", min_age: 65, max_age: 200 },
};

export const ONBOARDING_INVESTING_YEAR_META: Record<
  T_OnboardingInvestingYears,
  { text: string; description: string }
> = {
  LESS_THAN_2: {
    text: "Less than 2 years",
    description: "You are still building your investing foundation.",
  },
  TWO_TO_FIVE: {
    text: "2-5 years",
    description: "You know the basics and are building repeatable habits.",
  },
  FIVE_TO_TEN: {
    text: "5-10 years",
    description: "You have invested through a few market environments.",
  },
  TEN_PLUS: {
    text: "10+ years",
    description: "You have long-cycle market experience.",
  },
};

export const ONBOARDING_HEARD_ABOUT_SOURCE_META: Record<
  T_OnboardingHeardAboutSource,
  {
    text: string;
    description?: string;
    icon: T_OnboardingOptionIcon;
    text_input?: T_OnboardingOptionTextInput;
  }
> = {
  REDDIT: {
    text: "Reddit",
    icon: "reddit",
  },
  TWITTER_X: {
    text: "Twitter / X",
    icon: "twitter_x",
  },
  FACEBOOK: {
    text: "Facebook",
    icon: "facebook",
  },
  INSTAGRAM: {
    text: "Instagram",
    icon: "instagram",
  },
  GOOGLE: {
    text: "Google Search",
    icon: "google",
  },
  TIKTOK: {
    text: "TikTok",
    icon: "tiktok",
  },
  YOUTUBE: {
    text: "YouTube",
    icon: "youtube",
  },
  APP_STORE: {
    text: "App Store",
    icon: "app_store",
  },
  DISCORD: {
    text: "Discord",
    icon: "discord",
  },
  STOCKTWITS: {
    text: "Stocktwits",
    icon: "stocktwits",
  },
  THREADS: {
    text: "Threads",
    icon: "threads",
  },
  WORD_OF_MOUTH: {
    text: "Word of mouth",
    icon: "word_of_mouth",
  },
  PODCAST: {
    text: "Podcast",
    icon: "podcast",
  },
  OTHER: {
    text: "Other",
    icon: "other",
    text_input: {
      placeholder: "Tell us where you heard about Lattice",
      optional: true,
      max_length: ONBOARDING_OTHER_SOURCE_MAX_LENGTH,
    },
  },
};

export function isOnboardingInterestedIndustry(
  value: string,
): value is T_OnboardingInterestedIndustry {
  switch (value) {
    case "artificial_intelligence":
    case "semiconductors":
    case "software_cloud":
    case "healthcare_biotech":
    case "clean_energy":
    case "financials_fintech":
    case "consumer_brands":
    case "real_estate":
    case "industrials_infrastructure":
    case "energy_commodities":
    case "defense_aerospace":
    case "crypto_digital_assets":
      return true;
    default:
      return false;
  }
}

export function isOnboardingInvestingYears(
  value: string,
): value is T_OnboardingInvestingYears {
  switch (value) {
    case "LESS_THAN_2":
    case "TWO_TO_FIVE":
    case "FIVE_TO_TEN":
    case "TEN_PLUS":
      return true;
    default:
      return false;
  }
}

export function isOnboardingHeardAboutSource(
  value: string,
): value is T_OnboardingHeardAboutSource {
  switch (value) {
    case "REDDIT":
    case "TWITTER_X":
    case "FACEBOOK":
    case "INSTAGRAM":
    case "GOOGLE":
    case "TIKTOK":
    case "YOUTUBE":
    case "APP_STORE":
    case "DISCORD":
    case "STOCKTWITS":
    case "THREADS":
    case "WORD_OF_MOUTH":
    case "PODCAST":
    case "OTHER":
      return true;
    default:
      return false;
  }
}

export function isOnboardingAgeGroup(
  value: string,
): value is T_OnboardingAgeGroup {
  switch (value) {
    case "under_18":
    case "18_24":
    case "25_34":
    case "35_44":
    case "45_54":
    case "55_64":
    case "65_plus":
      return true;
    default:
      return false;
  }
}

export function getOnboardingAgeGroupRange(
  value: string,
): T_OnboardingAgeRange | null {
  if (!isOnboardingAgeGroup(value)) return null;
  const range = ONBOARDING_AGE_GROUP_META[value];
  return { min_age: range.min_age, max_age: range.max_age };
}

export function getOnboardingAgeGroupValueForRange(
  range: T_OnboardingAgeRange,
): T_OnboardingAgeGroup | null {
  for (const value of ONBOARDING_AGE_GROUP_VALUES) {
    const candidate = ONBOARDING_AGE_GROUP_META[value];
    if (
      range.min_age === candidate.min_age &&
      range.max_age === candidate.max_age
    ) {
      return value;
    }
  }
  return null;
}

export const ONBOARDING_INTERESTED_INDUSTRY_META: Record<
  T_OnboardingInterestedIndustry,
  { text: string; description: string }
> = {
  artificial_intelligence: {
    text: "Artificial intelligence",
    description: "AI platforms, data centers, automation, and applied models",
  },
  semiconductors: {
    text: "Semiconductors",
    description: "Chip designers, equipment makers, and memory suppliers",
  },
  software_cloud: {
    text: "Software & cloud",
    description: "SaaS, cybersecurity, databases, and cloud infrastructure",
  },
  healthcare_biotech: {
    text: "Healthcare & biotech",
    description: "Drugmakers, medical devices, insurers, and diagnostics",
  },
  clean_energy: {
    text: "Clean energy",
    description: "Solar, batteries, grid technology, and renewables",
  },
  financials_fintech: {
    text: "Financials & fintech",
    description: "Banks, payments, brokerages, and financial platforms",
  },
  consumer_brands: {
    text: "Consumer brands",
    description: "Retail, restaurants, apparel, travel, and entertainment",
  },
  real_estate: {
    text: "Real estate",
    description: "REITs, housing, data centers, and property platforms",
  },
  industrials_infrastructure: {
    text: "Industrials & infrastructure",
    description: "Transportation, machinery, construction, and logistics",
  },
  energy_commodities: {
    text: "Energy & commodities",
    description: "Oil, gas, metals, mining, and commodity producers",
  },
  defense_aerospace: {
    text: "Defense & aerospace",
    description: "Aircraft, defense systems, space, and government suppliers",
  },
  crypto_digital_assets: {
    text: "Crypto & digital assets",
    description: "Bitcoin, exchanges, miners, and blockchain infrastructure",
  },
};

export function getOnboardingInterestedIndustryText(
  value: T_OnboardingInterestedIndustry,
): string {
  return ONBOARDING_INTERESTED_INDUSTRY_META[value].text;
}

const ONBOARDING_INTERESTED_INDUSTRY_OPTIONS: T_OnboardingOption[] =
  ONBOARDING_INTERESTED_INDUSTRY_VALUES.map((value) => ({
    value,
    ...ONBOARDING_INTERESTED_INDUSTRY_META[value],
  }));

const ONBOARDING_INVESTING_YEAR_OPTIONS: T_OnboardingOption[] =
  ONBOARDING_INVESTING_YEAR_VALUES.map((value) => ({
    value,
    ...ONBOARDING_INVESTING_YEAR_META[value],
  }));

const ONBOARDING_HEARD_ABOUT_SOURCE_OPTIONS: T_OnboardingOption[] =
  ONBOARDING_HEARD_ABOUT_SOURCE_VALUES.map((value) => ({
    value,
    ...ONBOARDING_HEARD_ABOUT_SOURCE_META[value],
  }));

const ONBOARDING_AGE_GROUP_OPTIONS: T_OnboardingOption[] =
  ONBOARDING_AGE_GROUP_VALUES.map((value) => ({
    value,
    text: ONBOARDING_AGE_GROUP_META[value].text,
  }));

const ONBOARDING_QUESTIONS = [
  {
    key: "age_group",
    question: "What's your age group?",
    options: ONBOARDING_AGE_GROUP_OPTIONS,
    select_multiple: false,
    is_mandatory: true,
  },
  {
    key: "investing_years",
    question: "How many years have you been investing?",
    description: "This helps Prism match the level of detail in explanations.",
    options: ONBOARDING_INVESTING_YEAR_OPTIONS,
    select_multiple: false,
    is_mandatory: true,
  },
  {
    key: "interested_industries",
    question: "Which industries are you interested in?",
    description:
      "Select all that apply. We'll use this to personalize your feed and alerts.",
    options: ONBOARDING_INTERESTED_INDUSTRY_OPTIONS,
    select_multiple: true,
    is_mandatory: true,
  },
  {
    key: "heard_about_us",
    question: "How did you find us?",
    description: "Optional, but it helps us understand where to focus.",
    options: ONBOARDING_HEARD_ABOUT_SOURCE_OPTIONS,
    select_multiple: false,
    is_mandatory: false,
    auto_advance: false,
  },
] satisfies T_OnboardingQuestion[];

export const ONBOARDING_QUESTIONNAIRE: T_OnboardingQuestionnaire = {
  num_questions: ONBOARDING_QUESTIONS.length,
  questions: ONBOARDING_QUESTIONS,
};

// ─── Conversational onboarding v2 (RAI-1029/1030) ──────────────────────────
// Holdings-ingest choice collected by the redesigned onboarding. Deliberately
// NOT part of ONBOARDING_QUESTIONS above: that array drives the LIVE v1
// questionnaire on both shipped clients, so v2-only questions are exported
// standalone for the conversational UI to script.

export const ONBOARDING_INGEST_CHOICE_VALUES = [
  "SNAPTRADE",
  "PHOTO",
  "FILE",
  "MANUAL",
] as const;

export const ONBOARDING_V2_STAGE_VALUES = [
  "started",
  "source_recorded",
  "ingest_choice_recorded",
  "completed",
] as const;

export const z_OnboardingHeardAboutSource = z.enum(
  ONBOARDING_HEARD_ABOUT_SOURCE_VALUES,
);
export const z_OnboardingIngestChoice = z.enum(ONBOARDING_INGEST_CHOICE_VALUES);
export const z_OnboardingV2Stage = z.enum(ONBOARDING_V2_STAGE_VALUES);
export const z_OnboardingV2SourceSelection = z.discriminatedUnion("value", [
  z.object({
    value: z.literal("OTHER"),
    other_text: z
      .string()
      .trim()
      .min(1)
      .max(ONBOARDING_OTHER_SOURCE_MAX_LENGTH),
  }),
  z.object({
    value: z_OnboardingHeardAboutSource.exclude(["OTHER"]),
  }),
]);

export type T_OnboardingIngestChoice =
  (typeof ONBOARDING_INGEST_CHOICE_VALUES)[number];
export type T_OnboardingV2SourceSelection = z.infer<
  typeof z_OnboardingV2SourceSelection
>;
export type T_OnboardingV2Stage = (typeof ONBOARDING_V2_STAGE_VALUES)[number];

export const ONBOARDING_INGEST_CHOICE_META: Record<
  T_OnboardingIngestChoice,
  { text: string; description?: string; icon: T_OnboardingOptionIcon }
> = {
  SNAPTRADE: {
    text: "Connect your brokerage",
    description: "Live sync via SnapTrade",
    icon: "brokerage",
  },
  PHOTO: {
    text: "Snap a photo",
    description: "A screenshot of your holdings works",
    icon: "photo",
  },
  FILE: {
    text: "Upload a file",
    description: "CSV or broker statement",
    icon: "file",
  },
  MANUAL: {
    text: "Add holdings manually",
    icon: "manual",
  },
};

export function isOnboardingIngestChoice(
  value: string,
): value is T_OnboardingIngestChoice {
  switch (value) {
    case "SNAPTRADE":
    case "PHOTO":
    case "FILE":
    case "MANUAL":
      return true;
    default:
      return false;
  }
}

export const ONBOARDING_V2_INGEST_QUESTION: T_OnboardingQuestion = {
  key: "holdings_ingest_choice",
  question: "How would you like to add your holdings?",
  options: ONBOARDING_INGEST_CHOICE_VALUES.map((value) => ({
    value,
    text: ONBOARDING_INGEST_CHOICE_META[value].text,
    description: ONBOARDING_INGEST_CHOICE_META[value].description,
    icon: ONBOARDING_INGEST_CHOICE_META[value].icon,
  })),
  select_multiple: false,
  is_mandatory: true,
};
